import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminListContact, adminListDemo, adminMarkContact, adminMarkDemo } from '../../lib/adminApi';
import type { AdminContactRow, AdminDemoRow, RequestStatus } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import SegmentedFilter from '../../components/Admin/SegmentedFilter';

/*
 * Velaigal /admin/requests — contact + demo request triage.
 *
 * Two tabs backed by two independent endpoints (adminListContact /
 * adminListDemo — different row shapes, contact has no `company`, demo has
 * no `phone`, see src/lib/adminApi.ts). RequestPanel below is generic over
 * the row shape so both tabs share the same filter/pagination/mark-handled
 * mechanics; `key`-ing each panel by tab makes React remount it (rather
 * than reuse state) on switch, so each tab always opens on Unhandled/page 1.
 */

type Tab = 'contact' | 'demo';

const TABS: Array<{ value: Tab; label: string }> = [
  { value: 'contact', label: 'Contact' },
  { value: 'demo', label: 'Demo' },
];

const STATUS_FILTERS: Array<{ value: RequestStatus; label: string }> = [
  { value: 'unhandled', label: 'Unhandled' },
  { value: 'all', label: 'All' },
];

// Requester lines past this length get collapsed behind "Show more" so the
// list stays scannable — full text is always one click away, never hidden.
const MESSAGE_TRUNCATE_LENGTH = 220;

interface RequestRowBase {
  id: string;
  name: string;
  email: string;
  message: string;
  handledAt: string | null;
  createdAt: string;
}

// Handled is the "resolved" positive state, so it's the one that gets the
// saffron accent chip — unhandled gets the neutral hairline override, same
// convention as StatusChip in src/pages/HR/MyJobs.tsx.
const HandledChip: React.FC<{ handled: boolean }> = ({ handled }) => (
  <span
    className="chip"
    style={handled ? undefined : { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
  >
    {handled ? 'Handled' : 'Unhandled'}
  </span>
);

const InlineSpinner: React.FC<{ label: string }> = ({ label }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={{ borderColor: 'rgba(26, 22, 20, 0.2)', borderTopColor: 'var(--color-ink)' }}
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

const LoadingBlock: React.FC = () => (
  <div role="status" className="py-16 text-center">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-6 text-sm text-ink-tertiary">Loading requests.</p>
  </div>
);

const ErrorBlock: React.FC<{ message: string; copy: string; onRetry: () => void }> = ({ message, copy, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">{copy}</h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const EmptyBlock: React.FC<{ copy: string }> = ({ copy }) => (
  <div className="bg-paper border border-hairline rounded-md p-8 text-center">
    <p className="text-ink">{copy}</p>
  </div>
);

interface RequestRowProps<T extends RequestRowBase> {
  row: T;
  markFn: (id: string, handled: boolean) => Promise<{ ok: true }>;
  onToggled: (id: string, handledAt: string | null) => void;
  extra?: (row: T) => React.ReactNode;
}

function RequestRow<T extends RequestRowBase>({ row, markFn, onToggled, extra }: RequestRowProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const isHandled = !!row.handledAt;
  const isLong = row.message.length > MESSAGE_TRUNCATE_LENGTH;
  const displayMessage = !isLong || expanded ? row.message : `${row.message.slice(0, MESSAGE_TRUNCATE_LENGTH)}…`;

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await markFn(row.id, !isHandled);
      onToggled(row.id, isHandled ? null : new Date().toISOString());
      toast.success(isHandled ? 'Marked unhandled.' : 'Marked handled.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update this request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="card">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <HandledChip handled={isHandled} />
          </div>
          <p className="font-display text-lg font-medium text-ink">{row.name}</p>
          <p className="text-sm text-ink-secondary mt-0.5">{row.email}</p>
          {extra?.(row)}
        </div>
        <span className="text-xs text-ink-tertiary flex-shrink-0">{formatRelativeDate(row.createdAt)}</span>
      </div>

      <div className="mt-4 pt-4 border-t border-hairline">
        <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-line">{displayMessage}</p>
        {isLong && (
          <button
            type="button"
            className="velaigal-link text-xs font-medium mt-2"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-hairline">
        <button
          type="button"
          className="btn-secondary"
          style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
          onClick={() => void handleToggle()}
          disabled={busy}
        >
          {busy ? <InlineSpinner label="Updating…" /> : isHandled ? 'Mark unhandled' : 'Mark handled'}
        </button>
      </div>
    </article>
  );
}

interface RequestPanelProps<T extends RequestRowBase> {
  fetchFn: (params: { status?: RequestStatus; page?: number }) => Promise<{ items: T[]; page: number; hasMore: boolean }>;
  markFn: (id: string, handled: boolean) => Promise<{ ok: true }>;
  emptyCopy: string;
  errorCopy: string;
  extra?: (row: T) => React.ReactNode;
}

function RequestPanel<T extends RequestRowBase>({ fetchFn, markFn, emptyCopy, errorCopy, extra }: RequestPanelProps<T>) {
  const [status, setStatus] = useState<RequestStatus>('unhandled');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Cancelled-guarded so a fast unmount (tab switch) or a stale in-flight
  // retry can't setState afterward — mirrors the load effect in
  // src/pages/HR/MyJobs.tsx.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetchFn({ status, page });
        if (cancelled) return;
        setItems(res.items);
        setHasMore(res.hasMore);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFn, status, page, loadNonce]);

  const handleStatusChange = (next: RequestStatus) => {
    setStatus(next);
    setPage(1);
  };

  // Re-fetch the current tab/status/page instead of mutating `items` in place. A
  // splice-in-place left this page's items empty (with pagination controls still
  // implying more pages) whenever a mark-handled emptied the last row of an
  // unhandled-only page — re-running the same fetch keeps items/hasMore/page in
  // sync with what the server actually has for this page.
  const handleToggled = () => {
    setLoadNonce((n) => n + 1);
  };

  return (
    <div>
      <div className="mb-6">
        <SegmentedFilter options={STATUS_FILTERS} value={status} onChange={handleStatusChange} ariaLabel="Filter by status" />
      </div>

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock message={error} copy={errorCopy} onRetry={() => setLoadNonce((n) => n + 1)} />
      ) : items.length === 0 ? (
        <EmptyBlock copy={emptyCopy} />
      ) : (
        <>
          <div className="space-y-4">
            {items.map((item) => (
              <RequestRow key={item.id} row={item} markFn={markFn} onToggled={handleToggled} extra={extra} />
            ))}
          </div>

          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="text-xs text-ink-tertiary">Page {page}</span>
            <button type="button" className="btn-secondary" onClick={() => setPage((p) => p + 1)} disabled={!hasMore}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const Requests: React.FC = () => {
  const [tab, setTab] = useState<Tab>('contact');

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">Admin</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">Requests</h1>

      <div className="mb-6">
        <SegmentedFilter options={TABS} value={tab} onChange={setTab} ariaLabel="Request type" />
      </div>

      {tab === 'contact' ? (
        <RequestPanel<AdminContactRow>
          key="contact"
          fetchFn={adminListContact}
          markFn={adminMarkContact}
          emptyCopy="No contact submissions match this filter."
          errorCopy="Couldn't load contact submissions."
        />
      ) : (
        <RequestPanel<AdminDemoRow>
          key="demo"
          fetchFn={adminListDemo}
          markFn={adminMarkDemo}
          emptyCopy="No demo requests match this filter."
          errorCopy="Couldn't load demo requests."
          extra={(row) => <p className="text-xs text-ink-tertiary mt-0.5">{row.company}</p>}
        />
      )}
    </div>
  );
};

export default Requests;
